/**
 * The two admin pivot studies, from bar data to a ready-to-add overlay.
 *
 * Both are admin-only. The gate is enforced HERE as well as on the toolbar button, because a
 * hidden button is a UI convenience and not a check — anything else holding a reference to these
 * builders would otherwise produce the overlay for any account.
 */

import { detectPivots, pivotDistancesFrom, PIVOT_HIGH, OHLC_HIGH, DEFAULT_PIVOT_STRENGTH } from './pivots';
import { findAdditionSeries } from './additionSeries';
import { findFibonacciPivotPairs, pivotMarkersFrom, projectedTargets } from './fibPivotPairs';

/** How many of the most recent bars may serve as bar 0. */

/**
 * How many whole spans forward the base pair is projected with a vertical line.
 *
 * Position 0 is P1 and position 1 is P2, so this draws the base span itself and then repeats it
 * twice more into the future — 0, 1, 2, 3.
 */
export const FORWARD_POSITIONS = 3;

/**
 * Line colour per group, cycled.
 *
 * With every group framed, the lines only mean anything if you can tell which group a line belongs
 * to. Hues are kept far apart rather than shaded, since neighbouring groups are the ones most
 * likely to overlap on the chart.
 */
export const GROUP_LINE_COLORS = [
  '#0d47a1',  // blue
  '#e65100',  // orange
  '#2e7d32',  // green
  '#6a1b9a',  // purple
  '#c62828',  // red
  '#00838f',  // teal
  '#4e342e',  // brown
  '#ad1457',  // pink
];

const dotFor = (pivot, dates, extra = {}) => ({
  date: dates[pivot.index],
  price: pivot.price,
  // Dots sit above highs and below lows so they never cover the extreme they mark.
  position: pivot.type === PIVOT_HIGH ? 'above' : 'below',
  ...extra,
});

/**
 * Reverse addition series counted back from a bar the reader chooses.
 *
 * <h2>The anchor is exact, and that is the change</h2>
 *
 * It used to try the three most recent bars and keep whichever produced the longest series. That
 * made the study self-optimising and the answer unpredictable: bar 0 could land on the bar before
 * the one the reader had in mind, and since every distance is measured FROM bar 0, moving it by one
 * moves every term. A series found that way is real, but it answers a question nobody asked.
 *
 * <p>Now the anchor is exactly {@code anchorIndex} — the bar that was clicked — and the search runs
 * backwards from it. If no series qualifies from that bar, the study says so rather than quietly
 * counting from a neighbour that worked.
 *
 * <h2>And then forward by the same distances</h2>
 *
 * Each term found on the left is mirrored to the right of the anchor: a term at 17 bars back also
 * marks 17 bars forward. Those are <b>projections, not findings</b> — nothing has been detected
 * there — so they are drawn in their own colour, hollow, and labelled {@code +17} rather than
 * {@code 17}. Sharing the style with the confirmed pivots would assert that a pivot exists on a
 * bar that may not even have prices yet.
 *
 * @param {number} [opts.anchorIndex] the bar to count back from. Defaults to the last bar, which
 *        is the only sensible answer when nothing has been selected — and is still ONE bar, not a
 *        window.
 * @param {string[]} [opts.extendedDates] the chart's padded axis. Forward terms that land past the
 *        last priced bar need it: the price series stops there, so a target beyond it has no date
 *        to sit on and simply cannot be drawn without one.
 * @returns {{overlay:object, lines:object[], summary:object}|null} null when nothing qualifies
 */
export function buildAdditionSeriesStudy(barData, { isAdmin = false, tolerance = 1, minTerms = 5, strength = DEFAULT_PIVOT_STRENGTH, anchorIndex, extendedDates = null } = {}) {
  if (!isAdmin) return null;
  if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc)) return null;
  const { dates, ohlc } = barData;
  if (dates.length === 0) return null;

  const { all: pivots } = detectPivots(ohlc, strength);
  if (pivots.length === 0) return null;

  const lastIndex = Math.min(dates.length, ohlc.length) - 1;
  // Clamped rather than refused. The chart paints padded whitespace bars past the data, so a click
  // out there resolves to an index with no prices; the last real bar is what it meant.
  const anchor = Number.isInteger(anchorIndex)
    ? Math.max(0, Math.min(anchorIndex, lastIndex))
    : lastIndex;

  const items = pivotDistancesFrom(pivots, anchor);
  const [top] = findAdditionSeries(items, { tolerance, minTerms });
  if (!top) return null;
  const best = { anchorIndex: anchor, series: top };

  const points = best.series.matched.map(m => dotFor(m.pivot, dates, {
    color: m.pivot.type === PIVOT_HIGH ? '#1b5e20' : '#b71c1c',
    label: String(m.term),
  }));

  // Bar 0 itself. Every other number on the chart is a distance FROM this bar, so without it the
  // series is a set of unanchored numbers and there is no way to see what they are counted from.
  // It is drawn even when the anchor is not a pivot, because it is a reference point, not a term.
  const anchorPivot = best.series.matched.find(m => m.pivot.index === best.anchorIndex);
  if (!anchorPivot) {
    points.unshift({
      date: dates[best.anchorIndex],
      price: ohlc[best.anchorIndex] ? ohlc[best.anchorIndex][OHLC_HIGH] : null,
      position: 'above',
      color: '#000000',
      label: '0',
    });
  }

  // ── the same distances, counted FORWARD from the anchor ──────────────────────────────────
  //
  // Two destinations, because the chart has two kinds of bar to the right of the anchor. A term
  // landing on a bar that has prices gets a dot on that bar. One landing past the last priced bar
  // has no price to sit at, so it gets a dashed vertical instead — the same way the Fibonacci
  // pivot study marks a target that has not arrived. Inventing a y for it would draw a price level
  // the study has no basis for, which is a stronger claim than "this date".
  const lastRealIndex = Math.min(dates.length, ohlc.length) - 1;
  const projectionDates = Array.isArray(extendedDates) && extendedDates.length > dates.length
    ? extendedDates
    : dates;
  const lines = [];
  let forwardOnChart = 0;
  let forwardProjected = 0;
  let forwardUnreachable = 0;

  best.series.terms.forEach((term) => {
    const index = best.anchorIndex + term;
    if (index <= lastRealIndex) {
      const bar = ohlc[index];
      if (!bar) { forwardUnreachable += 1; return; }
      points.push({
        date: dates[index],
        price: bar[OHLC_HIGH],
        position: 'above',
        // Its own colour and a hollow symbol: this is where a term FALLS, not a pivot that was
        // found there. The backward dots are detections; these are arithmetic.
        color: '#00838f',
        symbol: 'emptyCircle',
        label: `+${term}`,
      });
      forwardOnChart += 1;
    } else if (index <= projectionDates.length - 1) {
      lines.push({
        type: 'vLine',
        date: projectionDates[index],
        color: '#00838f',
        lineType: 'dashed',
        label: `+${term}\n${index - lastRealIndex} bars out`,
        id: `pivot-addition-fwd-${best.anchorIndex}-${term}`,
      });
      forwardProjected += 1;
    } else {
      // Past the padded axis as well. Counted rather than dropped in silence: "to the extent
      // possible" is only an honest description if the part that was not possible is reported.
      forwardUnreachable += 1;
    }
  });

  return {
    overlay: {
      type: 'pivotMarkers',
      label: `Addition series from bar 0 (${dates[best.anchorIndex]})`,
      points,
      color: '#1b5e20',
      id: `pivot-addition-${best.anchorIndex}-${best.series.seeds.join('-')}`,
    },
    lines,
    summary: {
      anchorIndex: best.anchorIndex,
      anchorDate: dates[best.anchorIndex],
      seeds: best.series.seeds,
      terms: best.series.terms,
      forwardOnChart,
      forwardProjected,
      forwardUnreachable,
      pivotCount: points.length,
    },
  };
}

/**
 * Forward Fibonacci ratio pairs across the whole chart.
 *
 * @returns {{overlay:object, summary:object}|null} null when nothing qualifies
 */
export function buildFibPivotStudy(barData, {
  isAdmin = false, tolerance = 1, minWindow = 20, maxWindow = 300, minMatches = 2, strength = DEFAULT_PIVOT_STRENGTH, maxPairs = 40, extendedDates = null, maxProjections = 12,
} = {}) {
  if (!isAdmin) return null;
  if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc)) return null;
  const { dates, ohlc } = barData;

  const { all: pivots } = detectPivots(ohlc, strength);
  if (pivots.length < 3) return null;

  const results = findFibonacciPivotPairs(pivots, { tolerance, minWindow, maxWindow, minMatches });
  if (results.length === 0) return null;

  // Strongest pairs only. Every qualifying pair on a long chart would cover the price action in
  // dots, and results are already ordered by how many ratios confirmed each pair.
  const kept = results.slice(0, maxPairs);
  const markers = pivotMarkersFrom(kept, { includeAnchors: true });

  const points = markers.map(m => dotFor(m, dates, {
    color: m.type === PIVOT_HIGH ? '#0d47a1' : '#e65100',
    label: m.label,
  }));

  // Vertical lines through the whole-number positions of every group's base span: 0 (P1), 1 (P2),
  // then the span repeated forward twice more. They give each group's ratio dots a visible frame —
  // 0.618 of what, and where that cycle would land if it repeated.
  //
  // Drawn for EVERY kept group. This was originally the strongest group alone, to keep the chart
  // readable, but that leaves every other group showing bare numbers with nothing to measure them
  // against — the dots of group two are unreadable without group two's frame, which is precisely
  // the information the lines carry. Density is handled by colouring per group and by dropping
  // exact duplicates instead of by drawing fewer groups.
  const lines = [];
  const seenLines = new Set();
  kept.forEach((pair, groupIndex) => {
    const color = GROUP_LINE_COLORS[groupIndex % GROUP_LINE_COLORS.length];
    for (let k = 0; k <= FORWARD_POSITIONS; k++) {
      const idx = pair.p1.index + k * pair.distance;
      if (idx < 0 || idx >= dates.length) break;   // past the end of the chart

      // Two groups often project the same position onto the same bar. One line is enough; a second
      // identical line is invisible on top of the first and only costs render time. Keyed on the
      // POSITION as well as the bar, because group A's 3 landing where group B's 1 lands is two
      // different statements about the same bar and both are worth seeing.
      // `continue`, not `return`: this loop sits inside a forEach callback, so returning would
      // abandon the rest of THIS GROUP's positions the first time one of them was already drawn.
      const key = `${idx}|${k}`;
      if (seenLines.has(key)) continue;
      seenLines.add(key);

      lines.push({
        type: 'vLine',
        date: dates[idx],
        color,
        label: `Fib ${k}`,
        id: `pivot-fib-line-${groupIndex}-${pair.p1.index}-${k}`,
      });
    }
  });

  // ── Incomplete sequences: ratios that land beyond the last bar carrying price ──
  //
  // Drawn into the chart's padded future space, which is why the extended dates are needed: the
  // price series itself ends at the last bar, so a target one bar later has no date to sit on.
  // Without them there is simply nowhere to put a projection and none are emitted.
  const projectionDates = Array.isArray(extendedDates) && extendedDates.length > dates.length
    ? extendedDates
    : dates;
  const lastRealIndex = dates.length - 1;
  const targets = projectedTargets(kept, lastRealIndex, projectionDates.length - 1)
    .slice(0, maxProjections);

  targets.forEach(t => {
    lines.push({
      type: 'vLine',
      date: projectionDates[t.index],
      // Dashed and in a distinct colour: this marks where a pivot WOULD complete the sequence,
      // not one that has. Drawing it like a confirmed level would assert something that has not
      // happened yet.
      color: '#d81b60',
      lineType: 'dashed',
      label: `${t.label}
+${t.barsAhead} bars`,
      id: `pivot-fib-proj-${t.index}-${t.ratio}`,
    });
  });

  return {
    overlay: {
      type: 'pivotMarkers',
      label: 'Fibonacci pivot pairs',
      points,
      color: '#0d47a1',
      id: `pivot-fib-${kept.length}-${markers.length}`,
    },
    lines,
    summary: {
      pairsFound: results.length,
      pairsUsed: kept.length,
      pivotCount: points.length,
      truncated: results.length > kept.length,
      lineCount: lines.length,
      groupsFramed: kept.length,
      projectionCount: targets.length,
      nextTargetBars: targets.length ? targets[0].barsAhead : null,
      baseSpan: kept[0] ? kept[0].distance : 0,
    },
  };
}
